 import React, {useEffect, useState} from "react";
import {Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Modal, Radio, Row, Select, Space,} from "antd";
import {EditOutlined, MinusCircleOutlined, PlusOutlined,} from "@ant-design/icons";
import {FormInstance} from "antd/es/form";
import styled from "@emotion/styled";
import {Helper} from "../helper-list/list";
import moment from "moment";
import { v4 as uuid } from "uuid";
import {changeArrayToStringInObject} from "../../../utils";
import {District} from "components/district";
import {HelperPaymentMethodSelect} from "components/helper-payment-method";
import {formatTimeTable, gender, languages, paymentMethod} from "utils/common";
import {useHelperPositions} from "../../../utils/helper-position";

export interface BankAccount {
  key?: string | number;
  id?: number;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
}

interface SearchPanelProps extends Partial<Helper> {
  onUpdate: (value: Partial<Helper>) => void;
  reload: () => void;
}

export const HelperDetails = ({
  onUpdate,
  reload,
  ...data
}: SearchPanelProps) => {
  const timeTableValue: string[] = [];

  if (data.timeTable) {
    for (const [key, value] of Object.entries(data.timeTable)) {
      if (typeof value === "boolean" && value) {
        timeTableValue.push(key);
      }
    }
  }

  const formatData = {
    uuid: data.uuid,
    nameCn: data.nameCn,
    nameEn: data.nameEn,
    gender: data.gender,
    position: data.position,
    language: data.language && data.language.split(","),
    idCardNo: data.idCardNo,
    idCardName: data.idCardName,
    dob: data.dob ? moment(data.dob) : null,
    dateOfVaccines: data.dateOfVaccines ? moment(data.dateOfVaccines) : null,
    typesOfVaccines: data.typesOfVaccines,
    phone: data.phone,
    email: data.email,
    district: data.district && data.district.split(","),
    address: data.address,
    status: data.status,
    height: data.height,
    weight: data.weight,
    emergencyContactPerson: data.emergencyContactPerson,
    emergencyContactPhone: data.emergencyContactPhone,
    bankDetails: data.bankDetails,
    dailyCareSkills: data.dailyCareSkills?.split(",") || null,
    professionalNursingSkills:
      data.professionalNursingSkills?.split(",") || null,
    advancedNursingSkills: data.advancedNursingSkills?.split(",") || null,
    personInCharge: data.personInCharge,
    timeTable: timeTableValue,
  };

  console.log(formatData)

  const editBasicInfoFormRef = React.createRef<FormInstance>();
  const [editBasicInfoForm] = Form.useForm();
  const [showEditBasicInfoModal, setShowEditBasicInfoModal] = useState(false);

  const editVaccineFormRef = React.createRef<FormInstance>();
  const [editVaccineForm] = Form.useForm();
  const [showEditVaccineModal, setShowEditVaccineModal] = useState(false);

  const editBankAccountFormRef = React.createRef<FormInstance>();
  const [editBankAccountForm] = Form.useForm();
  const [showEditBankAccountModal, setShowEditBankAccountModal] = useState(
    false
  );

  const editSkillFormRef = React.createRef<FormInstance>();
  const [editSkillForm] = Form.useForm();
  const [showEditSkillModal, setShowEditSkillModal] = useState(false);

  const editTimeTableFormRef = React.createRef<FormInstance>();
  const [editTimeTableForm] = Form.useForm();
  const [showEditTimeTableModal, setShowEditTimeTableModal] = useState(false);

  const { data: helperPositions } = useHelperPositions();

  const onEditFormClick = (type: string) => {
    switch (type) {
      case "basic":
        setShowEditBasicInfoModal(!showEditBasicInfoModal);
        break;
      case "vaccine":
        setShowEditVaccineModal(!showEditVaccineModal);
        break;
      case "bank":
        editBankAccountForm.setFieldsValue({bankDetails: formatData.bankDetails, uuid: formatData.uuid})
        setShowEditBankAccountModal(!showEditBankAccountModal);
        break;
      case "skill":
        setShowEditSkillModal(!showEditSkillModal);
        break;
      case "time":
        editTimeTableForm.setFieldsValue({ timeTable: timeTableValue });
        setShowEditTimeTableModal(!showEditTimeTableModal);
        break;
    }
  };

  const onEditModalSubmit = async (values: Partial<Helper>) => {
    // if (values.timeTable && values.timeTable.length > 0) {
    //   values.timeTable = values.timeTable.reduce((a: any, v: string) => ({...a, [v]: true}), {})
    // }


    // return console.log(changeArrayToStringInObject(values));

    if (values.dateOfVaccines) {
      values.dateOfVaccines = moment(values.dateOfVaccines).format("YYYY-MM-DD");
    }

    if (values.dob) {
      values.dob = moment(values.dob).format("YYYY-MM-DD");
    }

    if (values.timeTable) {
      const newTimeTable = formatTimeTable(values.timeTable || []);

      await onUpdate(
          changeArrayToStringInObject({
            uuid: values.uuid,
            timeTable: newTimeTable,
          })
      );
    } else {
      await onUpdate(changeArrayToStringInObject(values));
    }

    await reload();

    if (showEditBasicInfoModal) {
      await setShowEditBasicInfoModal(!showEditBasicInfoModal);
    }

    if (showEditVaccineModal) {
      await setShowEditVaccineModal(!showEditVaccineModal);
    }

    if (showEditBankAccountModal) {
      await setShowEditBankAccountModal(!showEditBankAccountModal);
    }

    if (showEditSkillModal) {
      await setShowEditSkillModal(!showEditSkillModal);
    }

    if (showEditTimeTableModal) {
      await setShowEditTimeTableModal(!showEditTimeTableModal);
    }
  };

  return (
    <>
      <SectionContainer>
        <SectionTitle>help information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("basic")} />
        </EditButton>
        <DataContainer>
          <DataTitle>name:</DataTitle>
          <DataContent>{data?.nameCn}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>name in english:</DataTitle>
          <DataContent>{data?.nameEn}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>gender:</DataTitle>
          <DataContent>{gender(data?.gender)}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>id card number:</DataTitle>
          <DataContent>{data?.idCardNo}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>name in id card:</DataTitle>
          <DataContent>{data?.idCardName}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>date of birth:</DataTitle>
          <DataContent>{data?.dob}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>height (cm):</DataTitle>
          <DataContent>{data?.height}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>weight (kg):</DataTitle>
          <DataContent>{data?.weight}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>rank:</DataTitle>
          <DataContent>{data?.position}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>language:</DataTitle>
          <DataContent>{languages(data?.language)}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>phone:</DataTitle>
          <DataContent>{data?.phone}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>email:</DataTitle>
          <DataContent>{data?.email}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>emergency contacts:</DataTitle>
          <DataContent>{data?.emergencyContactPerson}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>emergency contact phone number:</DataTitle>
          <DataContent>{data?.emergencyContactPhone}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>region:</DataTitle>
          <DataContent>{data?.district}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>full address:</DataTitle>
          <DataContent>{data?.address}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>vaccination</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("vaccine")}/>
        </EditButton>
        <DataContainer>
          <DataTitle>get vaccinated against covid:</DataTitle>
          <DataContent>{data?.typesOfVaccines ? "Yes" : "No"}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>types of vaccines / frequency:</DataTitle>
          <DataContent>{data?.typesOfVaccines}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>date of vaccination:</DataTitle>
          <DataContent>{data?.dateOfVaccines}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>banks</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("bank")} />
        </EditButton>
        <DataContainer>
          <Row>
            <Col span={4}>
              <DataTitle>the name of the bank</DataTitle>
            </Col>
            <Col span={4}>
              <DataTitle>account holder's name</DataTitle>
            </Col>
            <Col span={4}>
              <DataTitle>account number</DataTitle>
            </Col>
            <Col span={4}>
              <DataTitle>payment methods</DataTitle>
            </Col>
            <Col span={4}>
              <DataTitle>Default</DataTitle>
            </Col>
          </Row>
          {data?.bankDetails?.map((item, index) => (
            <Row key={item.bankName}>
              <Col span={4}>
                <DataContent>{item.bankName}</DataContent>
              </Col>
              <Col span={4}>
                <DataContent>{item.accountName}</DataContent>
              </Col>
              <Col span={4}>
                <DataContent>{item.bankNumber}</DataContent>
              </Col>
              <Col span={4}>
                <DataContent>{paymentMethod(item.paymentMethod)}</DataContent>
              </Col>
              <Col span={4}>
                <DataContent>{item.default ? 'Yes' : ''}</DataContent>
              </Col>
            </Row>
          ))}
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>skills</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("skill")} />
        </EditButton>
        <DataContainer>
          <DataTitle>daily care skills:</DataTitle>
          <DataContent>{data?.dailyCareSkills}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>professional nursing skills:</DataTitle>
          <DataContent>{data?.professionalNursingSkills}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>advanced nursing skills:</DataTitle>
          <DataContent>{data?.advancedNursingSkills}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>other:</DataTitle>
          <DataContent>{data?.otherSkill}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>timetable</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("time")} />
        </EditButton>
        <DataContainer>
          {/* <DataTitle>時間表:</DataTitle> */}
          <DataContent>
            <Checkbox.Group value={timeTableValue} disabled>
              <Row gutter={[16, 4]}>
                <Col flex={1}>
                  <div>Monday</div>
                  <Checkbox value="monAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="monPm">night</Checkbox>
                </Col>
                <Col flex={2}>
                  <div>Tuesday</div>
                  <Checkbox value="tueAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="tuePm">night</Checkbox>
                </Col>
                <Col flex={3}>
                  <div>Wednesday</div>
                  <Checkbox value="wedAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="wedPm">night</Checkbox>
                </Col>
                <Col flex={4}>
                  <div>Thursday</div>
                  <Checkbox value="thuAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="thuPm">night</Checkbox>
                </Col>
                <Col flex={5}>
                  <div>Friday</div>
                  <Checkbox value="friAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="friPm">night</Checkbox>
                </Col>
                <Col flex={6}>
                  <div>Saturday</div>
                  <Checkbox value="satAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="satPm">night</Checkbox>
                </Col>
                <Col flex={7}>
                  <div>Sunday</div>
                  <Checkbox value="sunAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="sunPm">night</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </DataContent>
        </DataContainer>
      </SectionContainer>

      <Modal
        title={"edit helper information"}
        visible={showEditBasicInfoModal}
        footer={null}
        onCancel={() => onEditFormClick("basic")}
      >
        <Form
          form={editBasicInfoForm}
          ref={editBasicInfoFormRef}
          layout="vertical"
          initialValues={formatData}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item
            name="nameCn"
            label="name"
            rules={[{ required: true, message: "please enter your name" }]}
          >
            <Input placeholder="name" />
          </Form.Item>

          <Form.Item
            name="nameEn"
            label="name in english"
            rules={[{ required: true, message: "please enter your name in english" }]}
          >
            <Input placeholder="name in english" />
          </Form.Item>

          <Form.Item
            name="gender"
            label="gender"
            rules={[{ required: true, message: "please enter your gender" }]}
          >
            <Radio.Group>
              <Radio value={1}>females</Radio>
              <Radio value={0}>male</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="idCardNo"
            label="id card no"
            rules={[{ required: true, message: "please enter you id card no" }]}
          >
            <Input placeholder="id card no" />
          </Form.Item>

          <Form.Item
            name="idCardName"
            label="name on id card"
            rules={[{ required: true, message: "please enter your name on id card" }]}
          >
            <Input placeholder="name on id card" />
          </Form.Item>

          <Form.Item
            name="dob"
            label="date of birth"
            rules={[{ required: true, message: "please enter your date of birth" }]}
          >
            <DatePicker format={"DD/MM/YYYY"} placeholder="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="height"
            label="height (cm)"
            rules={[{ required: true, message: "please enter your height" }]}
          >
            <InputNumber
              min={0}
              max={250}
              style={{ width: 180 }}
              placeholder="height (cm)"
            />
          </Form.Item>

          <Form.Item
            name="weight"
            label="weight (kg)"
            rules={[{ required: true, message: "please enter your weight" }]}
          >
            <InputNumber
              min={0}
              max={300}
              style={{ width: 180 }}
              placeholder="weight (kg)"
            />
          </Form.Item>

          <Form.Item
            name="position"
            label="rank"
            rules={[{ required: true, message: "please enter helper rank" }]}
          >
            <Select placeholder={"rank"} style={{ width: "100%" }}>
              {(helperPositions?.list || []).map((item) => (
                <Select.Option key={item.uuid} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="language"
            label="language"
            rules={[{ required: true, message: "please enter a language" }]}
          >
            <Select mode="multiple" placeholder="language">
              <Select.Option value="cantonese">cantonese</Select.Option>
              <Select.Option value="english">english</Select.Option>
              <Select.Option value="putonghua">mandarin</Select.Option>
              {/*<Select.Option value="潮洲話">潮洲話</Select.Option>*/}
              {/*<Select.Option value="上海話">上海話</Select.Option>*/}
              {/*<Select.Option value="福建話">福建話</Select.Option>*/}
              {/*<Select.Option value="客家話">客家話</Select.Option>*/}
            </Select>
          </Form.Item>

          <Form.Item
            name="phone"
            label="phone"
            rules={[{ required: true, message: "please enter a phone number" }]}
          >
            <Input placeholder="phone" />
          </Form.Item>

          <Form.Item name="email" label="email">
            <Input placeholder="email" />
          </Form.Item>

          <Form.Item
            name="emergencyContactPerson"
            label="emergency contacts"
            rules={[{ required: true, message: "please enter an emergency contact" }]}
          >
            <Input placeholder="emergency contacts" />
          </Form.Item>

          <Form.Item
            name="emergencyContactPhone"
            label="emergency contact phone number"
            rules={[{ required: true, message: "please enter an emergency contact number" }]}
          >
            <Input placeholder="emergency contact phone number" />
          </Form.Item>

          <Form.Item
            name="district"
            label="region"
            rules={[{ required: true, message: "please enter region" }]}
          >
            <District />
          </Form.Item>

          <Form.Item
            name="address"
            label="full address"
            rules={[{ required: true, message: "please enter your full address" }]}
          >
            <Input placeholder="full address" />
          </Form.Item>

          {/* <Upload
            action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
            listType="picture"
          >
            <Button icon={<UploadOutlined />}>上傳幫手身分證</Button>
          </Upload> */}

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("basic")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit vaccinations"}
        visible={showEditVaccineModal}
        footer={null}
        onCancel={() => onEditFormClick("vaccine")}
      >
        <Form
            form={editVaccineForm}
            ref={editVaccineFormRef}
            layout="vertical"
            initialValues={formatData}
            onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid"/>
          </Form.Item>

          {/*<Form.Item label="接種新冠肺炎疫苗">*/}
          {/*  <Radio.Group>*/}
          {/*    <Radio value="y">有</Radio>*/}
          {/*    <Radio value="n">沒有</Radio>*/}
          {/*  </Radio.Group>*/}
          {/*</Form.Item>*/}

          <Form.Item name="typesOfVaccines" label="types of vaccines / frequency">
            <Input placeholder="types of vaccines / frequency"/>
          </Form.Item>

          <Form.Item name="dateOfVaccines" label="date of vaccination">
            <DatePicker placeholder="date of vaccination"/>
          </Form.Item>

          <Button
              htmlType="button"
              onClick={() => onEditFormClick("vaccine")}
              style={{marginRight: 20}}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit bank account"}
        visible={showEditBankAccountModal}
        footer={null}
        onCancel={() => onEditFormClick("bank")}
        width={900}
      >
        <Form
          form={editBankAccountForm}
          ref={editBankAccountFormRef}
          layout="vertical"
          onFinish={onEditModalSubmit}
          onValuesChange={(changedValues, allValues) => {
            let itemIndex = 0;
            let isUpdateDefault = false;

            for (let i = 0; i < changedValues.bankDetails.length; i++) {
              if (changedValues.bankDetails[i] && changedValues.bankDetails[i].default !== undefined) {
                itemIndex = i;
                isUpdateDefault = true;
                break;
              }
            }

            if (isUpdateDefault) {
              let newBankDetails = [];
              let bankDetails = allValues?.bankDetails || [];


              for (let i = 0; i < bankDetails.length; i++) {
                if (i === itemIndex) {
                  newBankDetails.push(bankDetails[i]);
                } else {
                  newBankDetails.push({
                    ...bankDetails[i],
                    default: false,
                  });
                }
              }

              editBankAccountForm.setFieldsValue({
                ...allValues,
                bankDetails: newBankDetails
              });
            }
          }}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.List name="bankDetails">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline">
                    <Form.Item
                      {...field}
                      key={uuid()}
                      label="the name of the bank"
                      name={[field.name, "bankName"]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      key={uuid()}
                      label="account holder's name"
                      name={[field.name, "accountName"]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      key={uuid()}
                      label="account number"
                      name={[field.name, "bankNumber"]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      key={uuid()}
                      label="grain delivery method"
                      style={{ width: 140 }}
                      name={[field.name, "paymentMethod"]}
                    >
                      <HelperPaymentMethodSelect />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      key={uuid()}
                      label=" "
                      valuePropName="checked"
                      name={[field.name, "default"]}
                    >
                      <Checkbox>Default</Checkbox>
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    bank account
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("bank")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"editing skills"}
        visible={showEditSkillModal}
        footer={null}
        onCancel={() => onEditFormClick("skill")}
        width={1100}
      >
        <Form
          form={editSkillForm}
          ref={editSkillFormRef}
          layout="vertical"
          initialValues={formatData}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item name="dailyCareSkills" label="daily care skills">
            <Checkbox.Group style={{ width: "100%" }}>
              <Row>
                <Col span={4}>
                  <Checkbox value="simple cooking">simple cooking</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="diaper changes">diaper changes</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="hug">hug</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="feeding">feeding</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="reminder to take medication">reminder to take medication</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="bathes">bathes</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item name="professionalNursingSkills" label="專業護理技能">
            <Checkbox.Group style={{ width: "100%" }}>
              <Row>
                <Col span={6}>
                  <Checkbox value="stomach and throat feeding">stomach and throat feeding</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="post operative care">post operative care</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="simple wound management">simple wound management</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="measure the signs of life">measure the signs of life</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="check blood sugar, urine">
                    check blood sugar, urine
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="dispose of urine bags">dispose of urine bags</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="PEG">PEG</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="ventilators">ventilators</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="peritoneal dialysis">peritoneal dialysis</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="spiritual services/pain management">
                    spiritual services/pain management
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="hospitalization, admissions and transfer skills">
                    hospitalization, admissions and transfer skills
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="rehabilitation exercises">rehabilitation exercises</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="massage with certificate">
                    massage with certificate）
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="clean bed and change linen">
                    clean bed and change linen
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="wound care and replacement of hemostatic pads">
                    wound care and replacement of hemostatic pads
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="stoma care（including：abdominal stoma/colostomy care)">
                    stoma care（including：abdominal stoma/colostomy care)
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="injections and administration of medications">
                    injections and administration of medications
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="basic first aid（there are certificates）">
                    basic first aid（there are certificates）
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item  name="advancedNursingSkills"
                      label="advanced nursing skills">
            <Checkbox.Group style={{ width: "100%" }}>
              <Row>
                <Col span={6}>
                  <Checkbox value="cardiopulmonary revival（certificates）">
                    cardiopulmonary revival（certificates）
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="vaccinations and immunizations">
                    vaccinations and immunizations
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="Techniques to assist patients in preparing for X-rays and receiving ECG tests">
                    Techniques to assist patients in preparing for X-rays and receiving ECG tests
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="phlegm">phlegm</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="blood drawn">blood drawn</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="replace the stomach and throat">replace the stomach and throat</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="replace the urinary throat">replace the urinary throat</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="insulin shots">insulin shots</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="otherSkill"
            label="other"
            style={{width: 280}}
            // rules={[{ required: true, message: "請選擇日常護理技能" }]}
          >
            <Input />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("skill")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"timeTable"}
        visible={showEditTimeTableModal}
        footer={null}
        onCancel={() => onEditFormClick("time")}
        width={700}
      >
        <Form
          form={editTimeTableForm}
          ref={editTimeTableFormRef}
          layout="vertical"
          initialValues={formatData}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item name="timeTable">
            <Checkbox.Group>
              <Row gutter={[16, 4]}>
                <Col flex={1}>
                  <div>Monday</div>
                  <Checkbox value="monAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="monPm">night</Checkbox>
                </Col>
                <Col flex={2}>
                  <div>Tuesday</div>
                  <Checkbox value="tueAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="tuePm">night</Checkbox>
                </Col>
                <Col flex={3}>
                  <div>Wednesday</div>
                  <Checkbox value="wedAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="wedPm">night</Checkbox>
                </Col>
                <Col flex={4}>
                  <div>Thursday</div>
                  <Checkbox value="thuAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="thuPm">night</Checkbox>
                </Col>
                <Col flex={5}>
                  <div>Friday</div>
                  <Checkbox value="friAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="friPm">night</Checkbox>
                </Col>
                <Col flex={6}>
                  <div>Saturday</div>
                  <Checkbox value="satAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="satPm">night</Checkbox>
                </Col>
                <Col flex={7}>
                  <div>Sunday</div>
                  <Checkbox value="sunAm">early</Checkbox>
                  <br />
                  <br />
                  <Checkbox value="sunPm">night</Checkbox>
                </Col>
              </Row>`
            </Checkbox.Group>
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("time")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>
    </>
  );
};

const SectionContainer = styled.div`
  background-color: #ffffff;
  width: 100%;
  padding: 15px;
  margin-bottom: 10px;
  position: relative;
`;

const SectionTitle = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 6px;
`;

const EditButton = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;

const DataContainer = styled.div`
  // padding-top: 2px;
`;

const DataTitle = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #8d939e;
  padding-right: 5px;
`;

const DataContent = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #3c3e42;
  font-weight: 500;
`;
